'use client'

import { useForm } from '@tanstack/react-form'
import { z } from 'zod/v4'

import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  FieldSet,
  Input,
} from '@ui/web/components'
import { AvatarSelector } from '@ui/web/lib'
import { childAvatarList } from '@helper/utils/domain'

import type { HomeConnectRequestDetail } from './request-summary'

const FormSchema = z.object({
  name: z.string().trim().min(1, { message: 'Wajib isi.' }).max(100),
  image: z.string().optional(),
})

export interface CreateNewChildValues {
  name: string
  image?: string
}

interface CreateNewStepProps {
  detail: HomeConnectRequestDetail
  isSubmitting: boolean
  onConfirm: (values: CreateNewChildValues) => void
  onConnectExisting: () => void
  onBack: () => void
}

/**
 * "Tambahkan siswa ini ke akunmu" — the adopt path.
 *
 * Nothing new is created: the school's student account gains a family membership, and these fields
 * edit that same account. The name and avatar arrive prefilled from the school precisely so the
 * parent can correct them — a school roster often carries a formal name the family never uses.
 */
export function CreateNewStep({ detail, isSubmitting, onConfirm, onConnectExisting, onBack }: CreateNewStepProps) {
  const form = useForm({
    defaultValues: {
      name: detail.studentName,
      image: detail.studentImage ?? undefined,
    } as z.input<typeof FormSchema>,
    validators: { onSubmit: FormSchema },
    onSubmit: ({ value }) => {
      onConfirm({ name: value.name, image: value.image })
    },
  })

  return (
    <>
      <DialogHeader>
        <DialogTitle>Tambahkan murid ini ke akunmu</DialogTitle>
        <DialogDescription>Silakan edit data info dari sekolah sebelum konfirmasi, jika perlu.</DialogDescription>
      </DialogHeader>

      <form
        className='srd:flex srd:flex-col srd:gap-4'
        onSubmit={(event) => {
          event.preventDefault()
          event.stopPropagation()
          void form.handleSubmit()
        }}
      >
        <FieldSet disabled={isSubmitting}>
          <div className='srd:flex srd:w-full srd:items-center srd:justify-center srd:py-2'>
            <form.Field name='image'>
              {(field) => (
                <AvatarSelector
                  handleAvatarSelectAction={(avatar) => field.handleChange(avatar.src)}
                  avatarData={childAvatarList}
                  initialAvatarKey={childAvatarList.find((a) => a.src === field.state.value)?.key ?? undefined}
                  size={64}
                />
              )}
            </form.Field>
          </div>

          <form.Field name='name'>
            {(field) => (
              <Field>
                <FieldLabel>Nama</FieldLabel>
                <Input
                  value={field.state.value}
                  placeholder='Nama anak'
                  onBlur={field.handleBlur}
                  onChange={(event) => field.handleChange(event.target.value)}
                />
                {field.state.meta.errors.length > 0 && <FieldError errors={field.state.meta.errors} />}
              </Field>
            )}
          </form.Field>
        </FieldSet>

        <DialogFooter className='srd:flex-col srd:gap-y-2'>
          <Button type='submit' className='srd:w-full' loading={isSubmitting} disabled={isSubmitting}>
            Konfirmasi dan terima
          </Button>
          <Button
            type='button'
            variant='outline'
            className='srd:w-full'
            disabled={isSubmitting}
            onClick={onConnectExisting}
          >
            Hubungkan ke yang ada saja
          </Button>
          <Button type='button' variant='ghost' className='srd:w-full' disabled={isSubmitting} onClick={onBack}>
            Kembali ke permohonan
          </Button>
        </DialogFooter>
      </form>
    </>
  )
}
